import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Gift, Truck, Sparkles, X, ChevronUp, ChevronDown } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const kindIcon = { shipping: Truck, gift: Gift, discount: Sparkles };

const RewardsProgress = () => {
  const { subtotal, giftProductId, setGiftProductId } = useCart();
  const [tiers, setTiers] = useState([]);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    api.get("/rewards").then((r) => setTiers(r.data)).catch(() => {});
  }, []);

  const maxThreshold = tiers.length ? Math.max(...tiers.map((t) => t.threshold)) : 0;
  const progress = maxThreshold ? Math.min(100, (subtotal / maxThreshold) * 100) : 0;
  const nextTier = tiers.find((t) => subtotal < t.threshold);
  const unlockedGiftTier = tiers.find((t) => t.kind === "gift" && subtotal >= t.threshold);

  useEffect(() => {
    if (!unlockedGiftTier && giftProductId) setGiftProductId(null);
    // eslint-disable-next-line
  }, [unlockedGiftTier?.id, giftProductId]);

  // Auto-collapse once a gift is chosen so cart items are more visible
  useEffect(() => {
    if (giftProductId) setExpanded(false);
  }, [giftProductId]);

  if (!tiers.length) return null;

  return (
    <div
      className="relative border-b border-border flex-shrink-0"
      style={{ background: "linear-gradient(135deg, #FDEEF3 0%, #FBF7F4 45%, #EFEAF7 100%)" }}
      data-testid="rewards-progress"
    >
      {/* Progress section — compact */}
      <div className="px-4 pt-3 pb-2">
        <AnimatePresence mode="wait">
          {nextTier ? (
            <motion.div
              key="next"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="flex items-center gap-2.5 mb-2.5"
            >
              <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0 text-base ring-1 ring-border/40">
                {nextTier.emoji || "🎁"}
              </div>
              <p className="font-body text-[13px] leading-tight text-charcoal">
                Add <span className="text-rose font-medium">{formatINR(nextTier.threshold - subtotal)}</span> more for
                <span className="font-label uppercase tracking-[0.14em] text-mauve ml-1 text-[11px]">{nextTier.label}</span>
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="all"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-2.5 mb-2.5"
            >
              <motion.div
                animate={{ rotate: [0, -12, 12, -6, 0] }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0 text-base"
              >
                🎉
              </motion.div>
              <p className="font-body text-[13px] leading-tight text-charcoal">
                You've unlocked <span className="text-rose font-medium">every reward</span>
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="relative h-1.5 rounded-full bg-white/70 overflow-visible mb-2.5">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="absolute left-0 top-0 h-full rounded-full"
            style={{ background: "linear-gradient(90deg, #F3D6E2, #C9789A, #E6E2F5)" }}
          />
          {tiers.map((t) => {
            const pct = Math.min(100, (t.threshold / maxThreshold) * 100);
            const unlocked = subtotal >= t.threshold;
            const Icon = kindIcon[t.kind] || Gift;
            return (
              <motion.div
                key={t.id}
                initial={{ scale: 0.7 }}
                animate={{ scale: unlocked ? 1.05 : 1 }}
                transition={{ type: "spring", stiffness: 240, damping: 16 }}
                className={`absolute -top-1.5 -translate-x-1/2 w-5 h-5 rounded-full flex items-center justify-center border-2 transition-colors ${
                  unlocked ? "bg-rose text-white border-white shadow" : "bg-white text-mauve border-white/70"
                }`}
                style={{ left: `${pct}%` }}
                title={t.label}
              >
                {unlocked ? <Check size={10} strokeWidth={3} /> : <Icon size={9} />}
              </motion.div>
            );
          })}
        </div>

        <div className="relative h-4">
          {tiers.map((t) => {
            const pct = Math.min(100, (t.threshold / maxThreshold) * 100);
            return (
              <span
                key={t.id}
                className={`absolute top-0 -translate-x-1/2 font-label text-[9px] uppercase tracking-[0.16em] whitespace-nowrap ${
                  subtotal >= t.threshold ? "text-rose" : "text-mauve"
                }`}
                style={{ left: `${pct}%` }}
              >
                ₹{Math.round(t.threshold / 1000)}k
              </span>
            );
          })}
        </div>
      </div>

      {/* Compact gift picker */}
      <AnimatePresence initial={false}>
        {unlockedGiftTier && (unlockedGiftTier.gift_products || []).length > 0 && (
          <motion.div
            key="gift-wrap"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
            data-testid="gift-picker"
          >
            <div className="mx-4 mb-3">
              <div
                className="relative rounded-xl overflow-hidden bg-white"
                style={{ boxShadow: "0 0 0 1px rgba(212,184,150,0.45)" }}
              >
                {/* Header row — compact, single line + toggle */}
                <button
                  type="button"
                  onClick={() => setExpanded((e) => !e)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-blush/20 transition"
                  data-testid="gift-picker-toggle"
                >
                  <motion.span
                    animate={{ rotate: [0, -6, 6, -3, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, repeatDelay: 3.5, ease: "easeInOut" }}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-base flex-shrink-0"
                    style={{ background: "linear-gradient(135deg, #F3D6E2, #E6E2F5)" }}
                  >
                    🎁
                  </motion.span>
                  <div className="flex-1 min-w-0">
                    <p className="font-label text-[10px] uppercase tracking-[0.2em] text-rose leading-none mb-0.5">
                      Free gift unlocked
                    </p>
                    <p className="font-cormorant text-[15px] leading-tight text-charcoal truncate">
                      {giftProductId
                        ? "Gift added — tap to change"
                        : "Tap to pick your complimentary piece"}
                    </p>
                  </div>
                  {giftProductId && <Check size={14} className="text-rose flex-shrink-0" />}
                  {expanded ? (
                    <ChevronUp size={16} className="text-mauve flex-shrink-0" />
                  ) : (
                    <ChevronDown size={16} className="text-mauve flex-shrink-0" />
                  )}
                </button>

                {/* Gift options grid — tight */}
                <AnimatePresence initial={false}>
                  {expanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="px-3 pb-3 pt-1 border-t border-border/40 bg-blush/10">
                        <div className="grid grid-cols-2 gap-2">
                          {unlockedGiftTier.gift_products.map((g, idx) => {
                            const selected = giftProductId === g.id;
                            return (
                              <motion.button
                                key={g.id}
                                type="button"
                                initial={{ opacity: 0, scale: 0.96 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: idx * 0.07, type: "spring", stiffness: 260, damping: 22 }}
                                whileHover={{ y: -2 }}
                                whileTap={{ scale: 0.97 }}
                                onClick={() => setGiftProductId(selected ? null : g.id)}
                                className={`group relative rounded-lg overflow-hidden text-left transition-all flex items-center gap-2.5 bg-white p-1.5 pr-3 ${
                                  selected
                                    ? "ring-2 ring-charcoal shadow"
                                    : "ring-1 ring-border/60 hover:ring-charcoal/40"
                                }`}
                                data-testid={`gift-option-${g.slug}`}
                              >
                                {/* thumbnail */}
                                <div className="relative w-14 h-14 rounded-md overflow-hidden bg-blush/40 flex-shrink-0">
                                  {(g.images || [])[0] && (
                                    <img
                                      src={g.images[0]}
                                      alt={g.name}
                                      className="w-full h-full object-cover"
                                    />
                                  )}
                                  <span
                                    className="absolute top-0.5 left-0.5 px-1.5 py-0.5 rounded-full font-label text-[8px] uppercase tracking-[0.18em] text-charcoal shadow-sm"
                                    style={{ background: "linear-gradient(135deg, #E8CDA3, #D4B896)" }}
                                  >
                                    Free
                                  </span>
                                </div>
                                {/* text */}
                                <div className="flex-1 min-w-0">
                                  <p className="font-cormorant text-[13px] leading-tight text-charcoal line-clamp-2">
                                    {g.name}
                                  </p>
                                  <p className="text-[10px] text-mauve mt-0.5">
                                    Worth <span className="line-through">{formatINR(g.price)}</span>
                                  </p>
                                </div>
                                {/* selected check */}
                                <AnimatePresence>
                                  {selected && (
                                    <motion.div
                                      initial={{ scale: 0, rotate: -45 }}
                                      animate={{ scale: 1, rotate: 0 }}
                                      exit={{ scale: 0 }}
                                      transition={{ type: "spring", stiffness: 320, damping: 20 }}
                                      className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-charcoal text-cream flex items-center justify-center shadow-md"
                                    >
                                      <Check size={11} strokeWidth={3} />
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </motion.button>
                            );
                          })}
                        </div>

                        {giftProductId && (
                          <motion.button
                            type="button"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            onClick={() => setGiftProductId(null)}
                            className="mt-2 inline-flex items-center gap-1 font-label text-[10px] uppercase tracking-[0.2em] text-mauve hover:text-rose"
                          >
                            <X size={10} /> Skip gift
                          </motion.button>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RewardsProgress;
